import {
  ButtonGroup,
  Select,
  SelectOption,
  StyledText,
} from '@leasequery/lq.react-component-library';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { BUTTON_GROUP_OPTIONS } from '../../../util/constants';
import { fetchAllocationStructuresWithCache } from '../../../api/report/fetchAllocationStructure';
import { Icon } from '../../../components';
import type { SplitByAllocationSwitchState, SplitByDimensionOrAllocationState } from './SplitByDimensionOrAllocation';
import * as Styled from './styled';
import { useAuth } from '../../../context';
import { AllocationStructureItem } from '../../../models/allocationStructureItem';
import { useTheme } from 'styled-components';

export interface SplitByAllocationState extends SplitByAllocationSwitchState {
  selectedOrgStructureId?: string | null;
}

interface SplitByAllocationProps {
  onChange: (updatedValue: SplitByAllocationState) => void;
  splitState: SplitByDimensionOrAllocationState;
}


function SplitByAllocation({
  onChange,
  splitState = {},
}: Readonly<SplitByAllocationProps>): JSX.Element {
  const { client, user } = useAuth();
  const clientId = client ? client.clientId : user.clientId;
  const {
    splitByAllocationSwitchValue = BUTTON_GROUP_OPTIONS[0],
    selectedOrgStructureId,
  } = splitState;
  const theme = useTheme();
  const [orgStructureOptions, setOrgStructureOptions] = useState<SelectOption<string>[]>([]);
  
  const splitStateRef = useRef(splitState);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    splitStateRef.current = splitState;
    onChangeRef.current = onChange;
  }, [splitState, onChange]);

  useEffect(() => {
    fetchAllocationStructuresWithCache(clientId)
      .then((structures: AllocationStructureItem[]) => {
        const options = (structures || []).map((s) => ({
          value: String(s.id),
          label: s.name,
          key: s.id,
        }));

        setOrgStructureOptions(options);
      })
      .catch(() => {
        setOrgStructureOptions([]);
      });
  }, [clientId]);

  useEffect(() => {
    if (!selectedOrgStructureId && orgStructureOptions.length > 0 && splitByAllocationSwitchValue.value) {
        onChangeRef.current?.({ ...splitStateRef.current, selectedOrgStructureId: orgStructureOptions[0].value });
    }
  }, [selectedOrgStructureId, orgStructureOptions, splitByAllocationSwitchValue.value]);

  const splitByAllocationSwitchChangeHandler = useCallback(
    (_, option: SelectOption<boolean>) => {
      onChange({ ...splitState, splitByAllocationSwitchValue: option });
    },
    [onChange, splitState],
  );

  const orgStructureChangeHandler = useCallback(
    (val: SelectOption<string>) => onChange({ ...splitState, selectedOrgStructureId: val.value }),
    [onChange, splitState],
  );

  const dropdownName = useId();

  return (
    <Styled.SplitByAllocation>
      <div>
        <Icon name="split" />
      </div>
      <div className="splitSection">
        <StyledText as="div" fontWeight="medium">
          Split by Allocation
        </StyledText>
        <StyledText as="div" className="splitDescription">
          Generates an additional sheet to your Excel export that splits each lease into multiple rows based on allocation percentages. Financial figures are weighted accordingly.
        </StyledText>
        {splitByAllocationSwitchValue.value && (
          <div className="splitByDimensionBySection">
            <span className="orgStructureLabel">
              Select Organizational Structure:
            </span>
            <div className="select-container">
              {
                orgStructureOptions.length > 0 &&
                  <Select
                    label=""
                    name={dropdownName}
                    options={orgStructureOptions}
                    value={orgStructureOptions.find(opt => opt.value === selectedOrgStructureId) || orgStructureOptions[0]}
                    onChange={orgStructureChangeHandler}
                    dropdownStyle={{ zIndex: theme.zIndexFloor.roof + theme.zIndexFloor.one, }}
                  />
              }
            </div>
          </div>
        )}
      </div>
      <div className="splitButtonSwitch">
        <ButtonGroup
          options={BUTTON_GROUP_OPTIONS}
          selectedOption={splitByAllocationSwitchValue}
          onClick={splitByAllocationSwitchChangeHandler}
          name="split-allocation-switch"
        />
      </div>
    </Styled.SplitByAllocation>
  );
}

export default SplitByAllocation;
